/// <amd-module name="SearchUI/Components/SearchPickList/SearchPickListItem" />
import * as React from "react";
import { Checkbox } from "VSSUI/Checkbox";
import { Tooltip } from "VSSUI/TooltipEx";
import { css, KeyCode } from "VSSUI/Util";
import { FocusWithin, IFocusWithinStatus } from "VSSUI/FocusWithin";
import { Location } from "VSSUI/Utilities/Position";
import { ISearchPickListItemProps } from "SearchUI/Components/SearchPickList/SearchPickList.Props";
import * as Resources from "SearchUI/Search.Resources.SearchUI";
import { FocusZoneContext } from "VSSUI/FocusZone";
import { getTabIndex } from "VSSUI/Utilities/Focus";

import "VSS/LoaderPlugins/Css!SearchUI/Components/SearchPickList/SearchPickListItem";

export interface ISearchPickListItemState {
    checked: boolean;
}

export class SearchPickListItem extends React.Component<ISearchPickListItemProps, ISearchPickListItemState> {
    constructor(props: ISearchPickListItemProps) {
        super(props);
        this.state = { checked: props.item.selected };
    }

    public render(): JSX.Element {
        const { disabled, itemIndex, itemsCount, focusableProps } = this.props;
        const { name, resultCount } = this.props.item;
        const { checked } = this.state;
        const itemAriaLabel = Resources.ItemWithCountLabel.replace("{0}", name).replace("{1}", resultCount.toString());

        return (
            <FocusWithin>
                {
                    (focusStatus: IFocusWithinStatus) => (
                        <FocusZoneContext.Consumer>
                            {
                                rowContext => {
                                    return (
                                        <div role="option"
                                            aria-selected={checked}
                                            aria-label={itemAriaLabel}
                                            aria-setsize={itemsCount}
                                            aria-posinset={itemIndex + 1}
                                            data-row-index={itemIndex}
                                            aria-disabled={disabled}
                                            data-focuszone={rowContext.focuszoneId}
                                            className={css("search-PickListItem--row", focusStatus.hasFocus && "focused")}
                                            onBlur={focusStatus.onBlur}
                                            tabIndex={getTabIndex(focusableProps)}
                                            onFocus={focusStatus.onFocus}
                                            onKeyDown={this.onKeyDown}>
                                            <div className={css("search-PickList--item", disabled && "is-disabled")} onClick={this.invoke} role={"menu"}>
                                                <Checkbox
                                                    excludeTabStop={true}
                                                    excludeFocusZone={true}
                                                    className="search-PickListItem--checkbox"
                                                    disabled={this.props.disabled}
                                                    checked={checked}
                                                    onChange={this.onCheckboxChange}
                                                    ariaLabel={name}
                                                    role="menuitemcheckbox" />
                                                {this.getLabelElement(name)}
                                                {
                                                    resultCount > 0
                                                        ? <span className="search-PickListItem--facet">{resultCount}</span>
                                                        : null
                                                }
                                            </div>
                                        </div>
                                    );
                                }
                            }
                        </FocusZoneContext.Consumer>
                    )
                }
            </FocusWithin>
        );
    }

    public componentDidMount(): void {
        if (this.props.onMount) {
            this.props.onMount();
        }
    }

    public componentWillReceiveProps(newProps: ISearchPickListItemProps): void {
        this.setState({ checked: newProps.item.selected });
    }

    private getLabelElement = (label: string): JSX.Element => {
        return (
            <Tooltip
                anchorOrigin={{ horizontal: Location.center, vertical: Location.end }}
                overflowOnly={true}
                text={label}
                tooltipOrigin={{ horizontal: Location.center, vertical: Location.start }}
            >
                <div className="search-PickListItem--labelContainer">
                    <span className="search-PickListItem--label">{label}</span>
                </div>
            </Tooltip>
        );
    }

    private onCheckboxChange = (ev: React.FormEvent<HTMLInputElement | HTMLElement>, isChecked: boolean): void => {
        const { onChange, item } = this.props;
        ev.stopPropagation();

        if (onChange) {
            onChange(item, isChecked);
        }
    }

    private onKeyDown = (evt: React.KeyboardEvent<HTMLElement>): void => {
        if (evt.keyCode === KeyCode.enter || evt.keyCode === KeyCode.space) {
            this.invoke(evt);
        }
    }

    private invoke = (evt: React.FormEvent<HTMLElement>): void => {
        if (!this.props.disabled) {
            const checked = !this.state.checked;
            this.setState({ checked: checked });

            this.onCheckboxChange(evt, checked);
        }
    }
}
